import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { ScreenSize } from 'libs/portal/src/layout/layout.service';
import { GenericService } from 'libs/portal/src/services/generic/generic.service';
import { ToastrService } from 'ngx-toastr';
import { ToolsService } from 'libs/portal/src/services/tools/tools.service';
import { UserRightsService } from 'libs/portal/src/services/user-rights/user-rights.service';
import { Endpoints } from '@huge-monorepo/portal/config';
import { CardTableOptions } from '../../tools/card-table/models';
import { CardTableHeader } from '../../tools/card-table/models/card-table-header.model';
import { GenericTableHeaderOptions, RefreshData } from '../../tools/card-table/models/generic-table-header-options.model';
import { openExportDialog } from '../../tools/export-modal/export-modal.component';

@Component({
  selector: 'app-device-usage-apn-report',
  templateUrl: './device-usage-apn-report.component.html',
  styleUrls: ['./device-usage-apn-report.component.scss'],
})
export class DeviceUsageApnReportComponent implements OnInit {
  date = new Date();

  deviceSearch = '';
  deviceCount = 10;
  deviceOffset = 0;
  deviceOrderBy = 'DeviceNum';
  deviceDesc = false;
  currentDevicePage = 1;
  totalDevices;

  devices = [];
  allDevicesSelected = false;
  selectedDevices = [];
  devicesLoading = true;
  downloading = false;
  obfuscateData = false;
  authSubscription: Subscription;
  ScreenSize = ScreenSize;
  search = false;

  cdrTableOptions: CardTableOptions = {
    titleProperty: 'name',
    subTitleProperty: '',
    additionalProperty: 'data',
    rowId: 'id',
    indicatorColorProperty: 'color',
    selectedProperty: 'selected',
    isSelectable: true,
    flexCardLayout: false,
    propertyRowExclusions: ['color', 'selected', 'id', 'bundle'],
    stylePropertyPrefix: 'devices',
    clickableFlexCard: false,
    textSize: 'regular',
  };

  headerOptions: GenericTableHeaderOptions = {
    ShowSearch: true,
    SearchPlaceholder: 'Search for devices',
    DefaultDesc: true,
    SortItems: [
      {
        Name: 'DeviceType',
        Display: 'Type',
      },
      {
        Name: 'CustomerTradingName',
        Display: 'Customer',
      },
      {
        Name: 'MerchantName',
        Display: 'Merchant',
      },
      {
        Name: 'TerminalID',
        Display: 'Terminal ID',
      },
    ],
    DateItem: {
      Singular: false,
      Title: 'Select date range',
      StartDate: this.tools.StartOfMonth().toDateString(),
      EndDate: new Date().toDateString(),
    },
    DateRequired: true,
    EnableExport: true,
    EnableSelectAll: true,
  };

  cdrTableHeaders: CardTableHeader[] = [
    {
      ID: 'name',
      Text: 'Name',
      Width: 10,
    },
    {
      ID: 'deviceType',
      Text: 'Type',
      Width: 10,
    },
    {
      ID: 'customer',
      Text: 'Customer',
      Width: 25,
    },
    {
      ID: 'MerchantName',
      Text: 'Merchant',
      Width: 25,
    },
    {
      ID: 'TerminalID',
      Text: 'Terminal ID',
      Width: 15,
    },
    {
      ID: 'SerialNumber',
      Text: 'Serial Number',
      Width: 10,
    },
  ];

  constructor(
    private endpoints: Endpoints,
    private auth: UserRightsService,
    private genericService: GenericService,
    private tools: ToolsService,
    private dialogRef: MatDialog,
    private toastService: ToastrService,
  ) {}

  ngOnInit() {
    this.authSubscription = this.auth.GetUser().subscribe((user) => {
      this.obfuscateData = user.isObfuscated;
      this.getDevices(this.deviceOrderBy, this.deviceSearch, this.deviceCount, this.deviceOffset, this.deviceDesc);
    });
  }

  ngOnDestroy(): void {
    this.authSubscription.unsubscribe();
  }

  getDevices(orderBy: string, search: string, count: number, offset: number, desc: boolean) {
    this.devicesLoading = true;
    const obj = {
      Online: null,
      BundleStart: null,
      BundleEnd: null,
      GroupID: this.auth.GetGroupID(),
    };

    this.genericService.POST(this.endpoints.GET_CDR_DEVICES(search, count, offset, orderBy, desc), obj).subscribe((resp) => {
      const deviceData = resp['body'];
      this.devices = [];
      if (deviceData != null) {
        const tmpData = [];
        deviceData.forEach((element) => {
          const tempObj = {
            id: element.id,
            name: `${element.name}`,
            deviceType: element.deviceType,
            customer: element.CustomerTradingName ? (this.obfuscateData ? this.tools.Obfuscate(element.CustomerTradingName, 'Customer', 8) : element.CustomerTradingName) : '',
            selected: false,
            bundle: element.InBundleAmount,
            color: '#949494',
            merchantName: element.MerchantName ?? '',
            terminalID: element.TerminalID ?? '',
            serialNumber: element.SerialNumber ?? '',
          };

          tmpData.push(tempObj);
        });

        setTimeout(() => {
          this.devices = tmpData;
        });
      } else {
        this.devices = [];
      }

      this.totalDevices = this.tools.ExtractPaginationFromHeaders(resp['headers']);

      this.devicesLoading = false;
    });
  }

  paginateDevices(pageIndex: number) {
    this.deviceOffset = (pageIndex - 1) * this.deviceCount;
    this.currentDevicePage = pageIndex;

    this.getDevices(this.deviceOrderBy, this.deviceSearch, this.deviceCount, this.deviceOffset, this.deviceDesc);
  }

  refreshTable(data: RefreshData): void {
    this.deviceSearch = data.Search ?? '';
    if (this.deviceSearch) {
      this.search = true;
    } else {
      this.search = false;
    }
    this.deviceOrderBy = data.Sort ?? '';
    this.deviceDesc = data.Desc ?? this.deviceDesc;
    this.deviceOffset = 0;
    this.currentDevicePage = 1;
    this.getDevices(this.deviceOrderBy, this.deviceSearch, this.deviceCount, this.deviceOffset, this.deviceDesc);
  }

  selectAllPressed(selectAll: boolean): void {
    this.allDevicesSelected = selectAll;
    if (!this.devices) return;
    this.devices.forEach((t) => {
      t.selected = selectAll;
      if (selectAll) {
        this.selectedDevices.push(t.id);
      } else {
        this.selectedDevices = [];
      }
    });
  }

  selectDevice(ev) {
    this.devices.find((t) => {
      if (t.id == ev.row.id) {
        t.selected = ev.checked;
        if (ev.checked) {
          this.selectedDevices.push(ev.row.id);
        } else {
          const removeIndex = this.selectedDevices.findIndex((t) => t == ev.row.id);
          this.selectedDevices.splice(removeIndex, 1);
        }
      }
    });
    this.allDevicesSelected = false;
  }

  onRowSelected(data: any) {
    if (data.selected) {
      // Add the ID if it isn't already in the array
      if (!this.selectedDevices.includes(data.id)) {
        this.selectedDevices.push(data.id);
      }
    } else {
      // Remove the ID if it exists in the array
      this.selectedDevices = this.selectedDevices.filter(id => id !== data.id);
    }
    this.allDevicesSelected = false;
  }

  exportReport(data: RefreshData) {
    if (this.allDevicesSelected || this.selectedDevices.length > 0) {
      let obj = {
        StartDate: data.FromDate,
        EndDate: data.ToDate,
        AllDevices: false, 
        DeviceIds: this.selectedDevices,
      };

      if(this.search == false){
        obj = {
          StartDate: data.FromDate,
          EndDate: data.ToDate,
          AllDevices: this.allDevicesSelected,
          DeviceIds: !this.allDevicesSelected ? this.selectedDevices : [],
        };
      }

      openExportDialog(this.dialogRef, {
        endpoint: this.endpoints.DOWNLOAD_DEVICE_USAGE_BY_APN_REPORT,
        reqObj: obj,
      });
    } else {
      this.toastService.info('', 'No devices selected', { timeOut: 1000 });
    }
  }
}
